import React, { type FC, useCallback, useMemo, useRef, useState, useEffect } from 'react'
import {
  Annotorious,
  ImageAnnotator,
  useAnnotations,
  useAnnotator,
  useSelection,
} from '@annotorious/react'
import type { ImageAnnotation, PolygonGeometry } from '@annotorious/react'
import type { AnnotoriousImageAnnotator } from '@annotorious/react'
import { ShapeType } from '@annotorious/react'
import '@annotorious/react/annotorious-react.css'
import { Button } from '@/shared/ui/Button/Button'
import { Select } from '@/shared/ui/Select/Select'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/Dialog/Dialog'
import { Trash } from 'lucide-react'
import { ActionIcon as MantineActionButton } from '@mantine/core'
import { Polygon } from '@/features/Photos/EditPhotoPolygonsModal/model/types'
import { useTranslation } from 'react-i18next'

export type PhotoPolygonEditorProps = {
  imageUrl: string
  initialPolygons?: Polygon[]
  onPolygonsChange?: (polygons: Polygon[]) => void
  buildings?: { id: number; name: string }[]
  getBuildingError?: (polygonId: string) => React.ReactNode
}

const percentToPx = (points: number[][], w: number, h: number) =>
  points.map(([xPct, yPct]) => [(xPct / 100) * w, (yPct / 100) * h])

const pxToPercent = (points: number[][], w: number, h: number) =>
  points.map(([x, y]) => [Math.round((x / w) * 10000) / 100, Math.round((y / h) * 10000) / 100])

const computeBounds = (pts: number[][]) => {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  pts.forEach(([x, y]) => {
    if (x < minX) minX = x
    if (y < minY) minY = y
    if (x > maxX) maxX = x
    if (y > maxY) maxY = y
  })
  return { minX, minY, maxX, maxY }
}

const EditorInner: FC<PhotoPolygonEditorProps> = ({
  imageUrl,
  initialPolygons = [],
  onPolygonsChange,
  buildings = [],
  getBuildingError,
}) => {
  const { t } = useTranslation(['photo', 'building', 'common'])
  const anno = useAnnotator<AnnotoriousImageAnnotator>()
  const annotations = useAnnotations(100)
  const selection = useSelection()
  const selectedPolygonId = selection.selected?.[0]?.annotation?.id ?? null
  const imgRef = useRef<HTMLImageElement | null>(null)
  const [imgSize, setImgSize] = useState<{ width: number; height: number } | null>(null)
  const [polygonBuildings, setPolygonBuildings] = useState<Record<string, number | null>>({})
  const [modalOpen, setModalOpen] = useState(false)
  const [pendingPolygonId, setPendingPolygonId] = useState<string | null>(null)
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null)

  const usedBuildingIds = useMemo(() => {
    const used = new Set()
    for (const bid of Object.values(polygonBuildings)) {
      if (bid != null) used.add(bid)
    }
    return used
  }, [polygonBuildings])

  const getBuildingOptionsForPolygon = useCallback(
    (_polygonId: string | null, includeBuildingId?: number | null) =>
      buildings
        .filter(
          b =>
            (includeBuildingId != null && b.id === includeBuildingId) || !usedBuildingIds.has(b.id),
        )
        .map(b => ({ value: String(b.id), label: b.name })),
    [buildings, usedBuildingIds],
  )

  useEffect(() => {
    if (!imageUrl) return
    const img = new Image()
    img.onload = () => setImgSize({ width: img.naturalWidth, height: img.naturalHeight })
    img.src = imageUrl
  }, [imageUrl])

  useEffect(() => {
    if (!anno || !imgSize) return

    const initialBuildingMap: Record<string, number | null> = {}
    initialPolygons.forEach(p => {
      initialBuildingMap[p.id] = p.building_id ?? null
    })
    setPolygonBuildings(initialBuildingMap)

    if (initialPolygons.length === 0) {
      anno.setAnnotations([], true)
      return
    }

    const annotationsToLoad: ImageAnnotation[] = initialPolygons.map(p => {
      const pointsPx = percentToPx(p.points, imgSize.width, imgSize.height)
      const bounds = computeBounds(pointsPx)

      return {
        id: p.id,
        bodies: [],
        target: {
          annotation: imageUrl,
          selector: {
            type: ShapeType.POLYGON,
            geometry: {
              points: pointsPx,
              bounds,
            },
          },
        },
      }
    })

    anno.setAnnotations(annotationsToLoad, true)
  }, [anno, imgSize, imageUrl, initialPolygons])

  useEffect(() => {
    if (!anno) return

    const handleCreate = (annotation: ImageAnnotation) => {
      const id = annotation?.id
      if (!id) return

      setPendingPolygonId(id)
      setSelectedBuildingId(null)
      setModalOpen(true)

      anno.setSelected(id, true)
    }

    anno.on('createAnnotation', handleCreate)
    return () => {
      anno.off('createAnnotation', handleCreate)
    }
  }, [anno])

  const selectPolygon = useCallback(
    (polygonId: string) => {
      anno?.setSelected(polygonId, true)
    },
    [anno],
  )

  const handleConfirmBuilding = () => {
    if (pendingPolygonId) {
      setPolygonBuildings(prev => ({
        ...prev,
        [pendingPolygonId]: selectedBuildingId,
      }))
    }
    setModalOpen(false)
    setPendingPolygonId(null)
  }

  const handleCancelBuilding = () => {
    if (pendingPolygonId && anno) {
      anno.removeAnnotation(pendingPolygonId)
    }
    setModalOpen(false)
    setPendingPolygonId(null)
  }

  const handleBuildingChange = (polygonId: string, buildingId: number | null) => {
    setPolygonBuildings(prev => ({
      ...prev,
      [polygonId]: buildingId,
    }))
  }

  const handleDeletePolygon = (polygonId: string) => {
    if (!anno) return
    anno.removeAnnotation(polygonId)

    setPolygonBuildings(prev => {
      const next = { ...prev }
      delete next[polygonId]
      return next
    })
  }

  const dataForBackend: Polygon[] = useMemo(() => {
    if (!imgSize) return []
    return annotations.map((ann: ImageAnnotation) => {
      console.log('ann', ann)
      const rawPoints = (ann.target.selector.geometry as unknown as PolygonGeometry).points ?? []
      return {
        id: ann.id,
        building_id: polygonBuildings[ann.id] ?? null,
        points: pxToPercent(rawPoints, imgSize.width, imgSize.height),
      }
    })
  }, [annotations, imgSize, polygonBuildings])

  useEffect(() => {
    if (!imgSize) return
    onPolygonsChange?.(dataForBackend)
  }, [dataForBackend, imgSize])

  return (
    <div className='flex flex-col lg:flex-row gap-4 w-full mt-2'>
      <div className='flex flex-col gap-2 flex-1 min-w-0'>
        <div className='relative w-full border border-gray-200 rounded-md overflow-hidden'>
          <ImageAnnotator tool='polygon' drawingEnabled drawingMode='click'>
            <img ref={imgRef} src={imageUrl} alt='Photo' className='block w-full h-auto' />
          </ImageAnnotator>
        </div>
      </div>

      <div className='flex flex-col gap-4 w-full lg:w-[520px] shrink-0'>
        <div className='flex flex-col gap-2'>
          <div className='text-sm font-semibold'>
            {t('photo:polygons')} ({annotations.length})
            {!imgSize && annotations.length > 0 && ` — ${t('photo:waiting_for_image_load')}`}
          </div>

          {annotations.length === 0 && (
            <div className='text-xs text-black/50'>{t('photo:start_drawing_polygon')}</div>
          )}

          <div className='flex flex-col gap-2 max-h-[45vh] overflow-auto pr-1'>
            {annotations.map((ann: ImageAnnotation, idx: number) => {
              const polygonId = ann.id
              const buildingId = polygonBuildings[polygonId] ?? null
              const isSelected = selectedPolygonId === polygonId
              const optionsForPolygon = getBuildingOptionsForPolygon(polygonId, buildingId)

              return (
                <div
                  key={polygonId}
                  role='button'
                  tabIndex={0}
                  onClick={() => selectPolygon(polygonId)}
                  className={[
                    'flex flex-row items-center gap-3 p-3 border rounded-md bg-white cursor-pointer',
                    isSelected ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200',
                  ].join(' ')}
                >
                  <div className='text-sm font-medium min-w-[70px]'>#{idx + 1}</div>

                  <div className='flex-1'>
                    <Select
                      name={`building-${polygonId}`}
                      label={t('common:building.sing')}
                      data={optionsForPolygon}
                      value={buildingId ? String(buildingId) : null}
                      onChange={value =>
                        handleBuildingChange(polygonId, value ? Number(value) : null)
                      }
                      clearable
                      className='w-full'
                      nothingFound={t('common:nothing_found')}
                      error={getBuildingError ? getBuildingError(polygonId) : undefined}
                    />
                  </div>

                  <MantineActionButton
                    variant='light'
                    size='sm'
                    className='bg-red hover:bg-red/80 text-white'
                    onClick={() => handleDeletePolygon(polygonId)}
                  >
                    <Trash size={14} />
                  </MantineActionButton>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>{t('photo:select_building_for_polygon')}</DialogTitle>
          </DialogHeader>
          <div className='flex flex-col gap-4 py-4'>
            <Select
              name='modal-building-select'
              label={t('common:building.sing')}
              data={getBuildingOptionsForPolygon(pendingPolygonId, selectedBuildingId)}
              value={selectedBuildingId ? String(selectedBuildingId) : null}
              onChange={value => setSelectedBuildingId(value ? Number(value) : null)}
              clearable
              nothingFound={t('common:nothing_found')}
            />
            <div className='flex flex-row gap-2 justify-end'>
              <Button type='button' size='md' color='gray' onClick={handleCancelBuilding}>
                {t('photo:buttons.cancel_and_delete')}
              </Button>
              <Button type='button' size='md' color='blue' onClick={handleConfirmBuilding}>
                {t('photo:buttons.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export const PhotoPolygonEditor: FC<PhotoPolygonEditorProps> = props => {
  return (
    <Annotorious>
      <EditorInner {...props} />
    </Annotorious>
  )
}
